/**
 * Payment Session Storage Utility
 *
 * Centralized handling of payment session data for redirect flows.
 * Used by Adyen (3DS), Klarna, and potentially other payment adapters.
 */

import type { PaymentAdapter } from './types';

// Session storage keys
const SESSION_KEYS = {
  ORDER_ID: 'norceCheckout_orderId',
  PAYMENT_ID: 'norceCheckout_paymentId',
  BASKET_UUID: 'norceCheckout_basketUuid',
  PAYMENT_ADAPTER: 'norceCheckout_paymentAdapter',
} as const;

export type PaymentSessionData = {
  orderId: string | null;
  paymentId: string | null;
  basketUuid: string | null;
  paymentAdapter: PaymentAdapter | null;
};

/**
 * Save payment session to sessionStorage
 * Called before any redirect that may leave the checkout page
 */
export function savePaymentSession(data: Partial<PaymentSessionData>): void {
  if (typeof window === 'undefined') return;

  if (data.orderId) {
    sessionStorage.setItem(SESSION_KEYS.ORDER_ID, data.orderId);
  }
  if (data.paymentId) {
    sessionStorage.setItem(SESSION_KEYS.PAYMENT_ID, data.paymentId);
  }
  if (data.basketUuid) {
    sessionStorage.setItem(SESSION_KEYS.BASKET_UUID, data.basketUuid);
  }
  if (data.paymentAdapter) {
    sessionStorage.setItem(SESSION_KEYS.PAYMENT_ADAPTER, data.paymentAdapter);
  }
}

/**
 * Get saved payment session from sessionStorage
 */
export function getPaymentSession(): PaymentSessionData {
  if (typeof window === 'undefined') {
    return {
      orderId: null,
      paymentId: null,
      basketUuid: null,
      paymentAdapter: null,
    };
  }

  return {
    orderId: sessionStorage.getItem(SESSION_KEYS.ORDER_ID),
    paymentId: sessionStorage.getItem(SESSION_KEYS.PAYMENT_ID),
    basketUuid: sessionStorage.getItem(SESSION_KEYS.BASKET_UUID),
    paymentAdapter: sessionStorage.getItem(SESSION_KEYS.PAYMENT_ADAPTER) as PaymentAdapter | null,
  };
}

/**
 * Clear payment session from sessionStorage
 * Called after successful redirect handling or on error
 */
export function clearPaymentSession(): void {
  if (typeof window === 'undefined') return;

  sessionStorage.removeItem(SESSION_KEYS.ORDER_ID);
  sessionStorage.removeItem(SESSION_KEYS.PAYMENT_ID);
  sessionStorage.removeItem(SESSION_KEYS.BASKET_UUID);
  sessionStorage.removeItem(SESSION_KEYS.PAYMENT_ADAPTER);
}

/**
 * Build success page URL with payment context
 * Provides consistent URL structure for all payment adapters
 */
export function buildSuccessUrl(
  baseUrl: string,
  basketUuid: string,
  data: {
    adapter?: PaymentAdapter | null;
    orderId?: string | null;
    paymentId?: string | null;
  } = {}
): string {
  const url = `${baseUrl}/checkout/success/${basketUuid}`;

  const params = new URLSearchParams();

  if (data.adapter) {
    params.set('adapter', data.adapter);
  }
  if (data.orderId) {
    params.set('orderId', data.orderId);
  }
  if (data.paymentId) {
    params.set('paymentId', data.paymentId);
  }

  const queryString = params.toString();
  return queryString ? `${url}?${queryString}` : url;
}

/**
 * Parse payment context from URL query params
 * Used on success page to determine what to display
 */
export function parsePaymentContext(query: Record<string, string | string[] | undefined>): {
  adapter: PaymentAdapter | null;
  orderId: string | null;
  paymentId: string | null;
} {
  return {
    adapter: (query.adapter as PaymentAdapter) || null,
    orderId: (query.orderId as string) || null,
    paymentId: (query.paymentId as string) || null,
  };
}
